import Card from "components/DesignSystem/Card/Card";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Table from "components/DesignSystem/Table/V2/Table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  CsAgent,
  CsNpsSummary,
  CsSortOrder,
  useGetCsNpsSectionQuery,
} from "store/apis/supportDashboard";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { CsStatCard } from "./CsStatCard";
import {
  CategoryTag,
  RatingDisplay,
  TableSkeletonRows,
} from "./CsOverviewCells";
import { RATING_SORT_OPTIONS, ratingRagStatus } from "./utils/csOverview";

type NpsTableMeta = {
  totalFeedbacks: number;
};

const columnHelper = createColumnHelper<CsAgent>();

const columns = [
  columnHelper.accessor("name", {
    header: "Agent",
    size: 18,
    cell: (info) => (
      <span className="t-text-body-sm t-font-medium t-text-text-100">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("filingRating", {
    header: "Filing rating",
    size: 13,
    cell: (info) => (
      <RatingDisplay
        rating={info.getValue()}
        ragStatus={ratingRagStatus(info.getValue())}
      />
    ),
  }),
  columnHelper.accessor("ticketRating", {
    header: "Ticket rating",
    size: 13,
    cell: (info) => (
      <RatingDisplay
        rating={info.getValue()}
        ragStatus={ratingRagStatus(info.getValue())}
      />
    ),
  }),
  columnHelper.accessor("totalFeedbacks", {
    header: "Total feedbacks",
    size: 14,
    cell: (info) => {
      const count = info.getValue();
      const { totalFeedbacks } = info.table.options.meta as NpsTableMeta;
      const pct =
        totalFeedbacks > 0 ? Math.round((count / totalFeedbacks) * 100) : 0;
      return (
        <span className="t-text-body-sm t-text-text-60">
          <span className="t-font-semibold">{count}</span>
          <span className="t-text-text-30 t-ml-1">({pct}%)</span>
        </span>
      );
    },
  }),
  columnHelper.accessor("bestFor", {
    header: "Top strengths",
    size: 13,
    cell: (info) => <CategoryTag label={info.getValue()} />,
  }),
  columnHelper.accessor("worstFor", {
    header: "Improvement areas",
    size: 13,
    cell: (info) => <CategoryTag label={info.getValue()} />,
  }),
];

type NpsStatCardsProps = {
  summary: CsNpsSummary;
};

const NpsStatCards = ({ summary }: NpsStatCardsProps) => {
  const filingCategorySub = [
    summary.bestFilingCategory && `Best: ${summary.bestFilingCategory}`,
    summary.worstFilingCategory && `Worst: ${summary.worstFilingCategory}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const ticketCategorySub = [
    summary.bestTicketCategory && `Best: ${summary.bestTicketCategory}`,
    summary.worstTicketCategory && `Worst: ${summary.worstTicketCategory}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="t-grid t-grid-cols-3 t-gap-3 t-mb-3">
      <CsStatCard label="Total feedbacks" value={summary.totalFeedbacks} />

      <CsStatCard
        label="Avg filing rating"
        value={
          <span>
            {summary.avgFilingRating === 0
              ? "-"
              : summary.avgFilingRating.toFixed(1)}
          </span>
        }
        sub={filingCategorySub || undefined}
      />

      <CsStatCard
        label="Avg ticket rating"
        value={
          <span>
            {summary.avgTicketRating === 0
              ? "-"
              : summary.avgTicketRating.toFixed(1)}
          </span>
        }
        sub={ticketCategorySub || undefined}
      />
    </div>
  );
};

type CsNpsSectionProps = {
  summary: CsNpsSummary;
  dateFrom: string;
  dateTo: string;
};

export const CsNpsSection = ({
  summary,
  dateFrom,
  dateTo,
}: CsNpsSectionProps) => {
  const query = useQuery();
  const { update: updateQuery } = useUpdateQuery();

  const page = Number(query.get("nps_page") || "1");
  const sortOrder = (query.get("nps_sort") as CsSortOrder) || "DSC";

  const handleSortChange = (option: { value: string } | null) => {
    if (option) updateQuery({ query: "nps_sort", value: option.value });
  };

  const { data, isFetching } = useGetCsNpsSectionQuery({
    start_date: dateFrom,
    end_date: dateTo,
    sort_key: "NPS_SCORE",
    sort_order: sortOrder,
    page_num: page,
  });

  const agents: CsAgent[] = data?.agents ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = data?.total_pages ?? 1;

  const table = useReactTable({
    data: agents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { size: 10, minSize: 1, maxSize: 100 },
    meta: { totalFeedbacks: summary.totalFeedbacks },
  });

  return (
    <div className="t-flex t-flex-col t-gap-3">
      <NpsStatCards summary={summary} />

      <Card.Root borderRounded="lg" className="t-overflow-hidden">
        <Card.Header>
          <Card.Title>NPS Ratings</Card.Title>
        </Card.Header>

        <Card.Body>
          <div className="t-flex t-items-center t-justify-between">
            <div className="t-flex t-items-center t-gap-1.5">
              <span className="t-text-body-sm t-text-text-30">Sort:</span>
              <Combobox
                size="small"
                isClearable={false}
                options={RATING_SORT_OPTIONS}
                value={RATING_SORT_OPTIONS.find((o) => o.value === sortOrder)}
                onChange={(o) =>
                  handleSortChange(o as { value: string } | null)
                }
              />
            </div>
            <PaginationHandler
              page_num={page}
              per_page={5}
              total_count={totalCount}
              total_pages={totalPages}
              onPageNumChange={(p) =>
                updateQuery({ query: "nps_page", value: String(p) })
              }
              isLoading={isFetching}
            />
          </div>
        </Card.Body>

        <Table.Container
          isLoading={isFetching}
          sticky={false}
          className="t-pt-4"
        >
          <Table.Content>
            <Table.Head>
              {table.getHeaderGroups().map((hg) => (
                <Table.HeadRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <Table.HeadCell
                      key={h.id}
                      style={{ width: `${h.getSize()}%` }}
                      isUpperCase={false}
                    >
                      {h.isPlaceholder
                        ? null
                        : flexRender(h.column.columnDef.header, h.getContext())}
                    </Table.HeadCell>
                  ))}
                </Table.HeadRow>
              ))}
            </Table.Head>
            <Table.Body>
              {isFetching ? (
                <TableSkeletonRows columns={columns} />
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}%` }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </Card.Root>
    </div>
  );
};
